import { RefObject, useLayoutEffect } from 'react';

/**
 * The hand-history snapshot frame HUGS what the table paints.
 *
 * The snapshot draws the real live table, and the live table is laid out for a
 * full game screen: its seat ring keeps 12% of the container's height above the
 * top seats for their hole cards and action bubble, and its bottom for the
 * hero's furniture. A recorded hand often leaves the top seats open, so that
 * room is empty — and a fixed reserve for a top seat's WINNER badge stacked
 * another 48px on top of it (screenshot #12, a ~100px empty band under the pot
 * carousel's title).
 *
 * Neither number can be right for every hand: the room a top seat needs depends
 * on whether it is open, holds cards, carries a bubble or glows as a winner. So
 * the frame does not guess. The table still sizes itself from its own box (the
 * stage, at the frame's width and aspect — see `.realTableStage`); this then
 * measures the union of what is actually painted inside it and crops the frame
 * to that, by setting the stage's top and bottom margins. Paint above or below
 * the box (a top seat's badge, a winner's glow) grows the frame instead, so it
 * is never clipped.
 *
 * Cropping moves no pixel of the table and changes none of its sizes: the stage
 * keeps its full box, so the table's measurement (useContainerSize) — and with
 * it the felt fit and seat scale — is exactly what it was.
 */

/** The stage's top margin: negative trims empty space, positive adds room. */
export const HUG_TOP_VAR = '--snapshot-hug-top';
/** The stage's bottom margin, the same way round. */
export const HUG_BOTTOM_VAR = '--snapshot-hug-bottom';

export type Box = { top: number; right: number; bottom: number; left: number };
type Edges = Box;

/** The computed style this needs, so a test can supply what jsdom cannot compute. */
export type PaintStyle = {
  display: string;
  visibility: string;
  opacity: string;
  overflow: string;
  overflowX: string;
  overflowY: string;
  backgroundColor: string;
  backgroundImage: string;
  borderTopWidth: string;
  borderRightWidth: string;
  borderBottomWidth: string;
  borderLeftWidth: string;
  borderTopStyle: string;
  borderRightStyle: string;
  borderBottomStyle: string;
  borderLeftStyle: string;
  boxShadow: string;
};

type Readers = {
  rectOf?: (el: Element) => DOMRect;
  styleOf?: (el: Element) => PaintStyle;
};

const NO_REACH: Edges = { top: 0, right: 0, bottom: 0, left: 0 };

/** Elements that paint their own box whatever their style says. */
const REPLACED_TAGS = new Set(['IMG', 'CANVAS', 'SVG', 'VIDEO', 'PICTURE']);

export const readPaintStyle = (el: Element): PaintStyle => {
  const style = window.getComputedStyle(el);
  return {
    display: style.display,
    visibility: style.visibility,
    opacity: style.opacity,
    overflow: style.overflow,
    overflowX: style.overflowX,
    overflowY: style.overflowY,
    backgroundColor: style.backgroundColor,
    backgroundImage: style.backgroundImage,
    borderTopWidth: style.borderTopWidth,
    borderRightWidth: style.borderRightWidth,
    borderBottomWidth: style.borderBottomWidth,
    borderLeftWidth: style.borderLeftWidth,
    borderTopStyle: style.borderTopStyle,
    borderRightStyle: style.borderRightStyle,
    borderBottomStyle: style.borderBottomStyle,
    borderLeftStyle: style.borderLeftStyle,
    boxShadow: style.boxShadow
  };
};

/** Splits a comma-separated CSS list, ignoring the commas inside `rgba(...)`. */
const splitTopLevel = (value: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of value) {
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;
    if (char === ',' && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts;
};

/**
 * A blur radius is twice the Gaussian's standard deviation (CSS Backgrounds 3),
 * and Chromium paints the blur out to three deviations: 1.5 x the radius.
 */
const BLUR_PAINT_FACTOR = 1.5;

/**
 * How far an element's OUTER box-shadows paint past its border box on each
 * side: the painted blur plus spread, moved by the offset. Inset shadows paint
 * inside the box and reach nothing. A winner's seat glow (`0 0 24px 8px`)
 * reaches 44px, its faint tail included.
 */
export const shadowReach = (boxShadow: string | undefined): Edges => {
  if (!boxShadow || boxShadow === 'none') return NO_REACH;

  return splitTopLevel(boxShadow).reduce<Edges>((reach, shadow) => {
    if (/\binset\b/.test(shadow)) return reach;
    const lengths = (shadow.replace(/[a-z-]+\([^)]*\)/gi, ' ').match(/-?\d*\.?\d+px/g) ?? []).map(
      parseFloat
    );
    if (lengths.length < 2) return reach;
    const [x, y, blur = 0, spread = 0] = lengths;
    const out = Math.max(blur, 0) * BLUR_PAINT_FACTOR + spread;
    return {
      top: Math.max(reach.top, out - y),
      right: Math.max(reach.right, out + x),
      bottom: Math.max(reach.bottom, out + y),
      left: Math.max(reach.left, out - x)
    };
  }, NO_REACH);
};

/**
 * True only for a colour with no alpha: "transparent", or an rgba()/rgb() whose
 * FOURTH component is 0. Solid black, "rgb(0, 0, 0)", also ends in a 0 — read
 * as transparent, a finished hand's black open-seat circle went unmeasured and
 * the frame cropped it down to its "Open" label.
 */
const isTransparent = (color: string) => {
  const value = color?.trim();
  if (!value || value === 'transparent') return true;
  const channels = value
    .match(/^rgba?\(([^)]*)\)$/i)?.[1]
    .split(/[\s,/]+/)
    .filter(Boolean);
  return channels?.length === 4 && parseFloat(channels[3]) === 0;
};

const hasBorder = (style: PaintStyle) =>
  (
    [
      [style.borderTopWidth, style.borderTopStyle],
      [style.borderRightWidth, style.borderRightStyle],
      [style.borderBottomWidth, style.borderBottomStyle],
      [style.borderLeftWidth, style.borderLeftStyle]
    ] as const
  ).some(([width, lineStyle]) => parseFloat(width) > 0 && !/^(none|hidden)?$/.test(lineStyle));

const hasOwnText = (el: Element) =>
  Array.from(el.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== ''
  );

/** Does this element put anything on screen itself (not via its children)? */
const paintsItself = (el: Element, style: PaintStyle) =>
  REPLACED_TAGS.has(el.tagName.toUpperCase()) ||
  hasOwnText(el) ||
  !isTransparent(style.backgroundColor) ||
  (!!style.backgroundImage && style.backgroundImage !== 'none') ||
  hasBorder(style) ||
  (!!style.boxShadow && style.boxShadow !== 'none');

const clipsChildren = (style: PaintStyle) =>
  [style.overflow, style.overflowX, style.overflowY].some((value) =>
    /\b(hidden|clip|auto|scroll)\b/.test(value ?? '')
  );

const intersect = (a: Box, b: Box | undefined): Box | undefined => {
  if (!b) return a;
  const box = {
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
    left: Math.max(a.left, b.left)
  };
  return box.bottom > box.top && box.right > box.left ? box : undefined;
};

const union = (a: Box | undefined, b: Box): Box =>
  a
    ? {
        top: Math.min(a.top, b.top),
        right: Math.max(a.right, b.right),
        bottom: Math.max(a.bottom, b.bottom),
        left: Math.min(a.left, b.left)
      }
    : b;

/**
 * The union of every box painted inside `root` (excluding `root` itself), in
 * viewport coordinates, or undefined when nothing is painted.
 *
 * "Painted" is what puts something on screen: text, an image or canvas, a
 * background, a border, an outer shadow (counted to its full reach). Layout
 * wrappers that only position their children — the full-size table container,
 * the felt's perspective wrapper — paint nothing and are walked through. A
 * subtree at opacity 0 or display none is skipped (the snapshot's parked dealer
 * chips), and anything inside an overflow-clipping ancestor counts only as far
 * as that ancestor shows it (the felt's spinning glow layer is far larger than
 * the oval that clips it).
 */
export const paintedBounds = (
  root: Element,
  { rectOf = (el) => el.getBoundingClientRect(), styleOf = readPaintStyle }: Readers = {}
): Box | undefined => {
  let bounds: Box | undefined;

  const walk = (el: Element, clip: Box | undefined) => {
    const style = styleOf(el);
    if (style.display === 'none' || parseFloat(style.opacity) === 0) return;

    const rect = rectOf(el);
    const box: Box = { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left };
    const hasArea = rect.width > 0 && rect.height > 0;

    if (hasArea && style.visibility !== 'hidden' && paintsItself(el, style)) {
      const reach = shadowReach(style.boxShadow);
      const painted = intersect(
        {
          top: box.top - reach.top,
          right: box.right + reach.right,
          bottom: box.bottom + reach.bottom,
          left: box.left - reach.left
        },
        clip
      );
      if (painted) bounds = union(bounds, painted);
    }

    // An SVG's parts are inside its own box; nothing to gain walking them.
    if (el.tagName.toUpperCase() === 'SVG') return;
    // With no clip yet, `intersect` returns the box itself; undefined here means
    // this clip lies wholly outside the inherited one, so nothing inside shows.
    const clips = hasArea && clipsChildren(style);
    const childClip = clips ? intersect(box, clip) : clip;
    if (clips && !childClip) return;
    Array.from(el.children).forEach((child) => walk(child, childClip));
  };

  Array.from(root.children).forEach((child) => walk(child, undefined));
  return bounds;
};

/**
 * The stage margins that crop the frame to `painted`. Negative trims empty
 * space off that edge; positive adds room for paint past the stage's box.
 * Nothing painted yet leaves the frame at the stage's own box.
 */
export const frameHug = (
  stage: Pick<Box, 'top' | 'bottom'>,
  painted: Box | undefined
): { top: number; bottom: number } => {
  if (!painted) return { top: 0, bottom: 0 };
  // `+ 0` normalises -0 so an exact fit writes "0px", not "-0px".
  return {
    top: Math.round(stage.top - painted.top) + 0,
    bottom: Math.round(painted.bottom - stage.bottom) + 0
  };
};

const writeVar = (el: HTMLElement, name: string, px: number) => {
  const value = `${px}px`;
  if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value);
};

/**
 * Keeps the stage's hug current. Measures once before the first paint, then
 * again (once per frame at most) whenever the table's DOM, size, fonts, images
 * or transitions change — the snapshot table is static, so this settles as soon
 * as the table has laid itself out.
 */
export const useSnapshotFrameHug = (stageRef: RefObject<HTMLElement>) => {
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    let frame = 0;
    let disposed = false;

    const measure = () => {
      frame = 0;
      if (disposed) return;
      const stageRect = stage.getBoundingClientRect();
      // No layout (not displayed, or a non-rendering environment): nothing to fit.
      if (stageRect.height <= 0) return;
      const hug = frameHug(stageRect, paintedBounds(stage));
      writeVar(stage, HUG_TOP_VAR, hug.top);
      writeVar(stage, HUG_BOTTOM_VAR, hug.bottom);
    };

    const schedule = () => {
      if (frame || disposed) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();

    // The stage's own attributes are ours (the hug just written); every other
    // change inside it is the table's.
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => record.target !== stage || record.type !== 'attributes')) {
        schedule();
      }
    });
    mutations.observe(stage, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true
    });

    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    resize?.observe(stage);

    stage.addEventListener('transitionend', schedule);
    stage.addEventListener('animationend', schedule);
    // `load` does not bubble; capture it so a late card image re-measures.
    stage.addEventListener('load', schedule, true);
    document.fonts?.ready.then(schedule).catch(() => undefined);

    return () => {
      disposed = true;
      if (frame) window.cancelAnimationFrame(frame);
      mutations.disconnect();
      resize?.disconnect();
      stage.removeEventListener('transitionend', schedule);
      stage.removeEventListener('animationend', schedule);
      stage.removeEventListener('load', schedule, true);
    };
  }, [stageRef]);
};
